/**
 * 토스트 표시기 (셸) — GameScene showToast/showCenterToast 추출 (일감 S0).
 * 하단 토스트(재화 부족 등 일시 알림)와 게임뷰 중앙 토스트(던전 실패 등)를 담당.
 */
import Phaser from "phaser";
import { NineSlicePanel } from "@yjsgames/phaser-ui";
import { centerToast } from "@yjsgames/phaser-fx";
import { FONT_FAMILY, TOAST_PANEL_STYLE, TYPO } from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { DEPTH } from "../objects/depthLayers.js";
import { sceneGameviewRect } from "./sceneLayout.js";

const TOAST_PANEL_WIDTH = 340;
const TOAST_PANEL_HEIGHT = 46;
const TOAST_ENTER_OFFSET_Y = 18;
const TOAST_ENTER_DURATION_MS = 180;
const TOAST_HOLD_MS = 1200;
const TOAST_TEXT_EXIT_DURATION_MS = 90;
const TOAST_TEXT_ALPHA_DELAY_MS = 65;
const TOAST_TEXT_ALPHA_DURATION_MS = 110;

const TOAST_TEXT_MAX_WIDTH = TOAST_PANEL_WIDTH - 56;
const HOLOGRAM_FADE_DURATION_MS = 160;
const HOLOGRAM_SCAN_DELAY_MS = 120;
const HOLOGRAM_SCAN_DURATION_MS = 310;
const HOLOGRAM_COLLAPSE_DURATION_MS = 210;
const HOLOGRAM_EDGE_LINE_ALPHA = 0.2;
const HOLOGRAM_CORNER_ALPHA = 0.78;
const HOLOGRAM_BEAM_ALPHA = 0.18;

export class ToastPresenter {
  constructor(private readonly scene: Phaser.Scene) {}

  /** 화면 중앙 하단 토스트 — 아래에서 진입 → 홀로그램 스캔 → 접히는 프로젝션 후 destroy(누적 없음). */
  showToast(message: string): void {
    const s = this.scene;
    const cx = s.scale.width / 2;
    const cy = s.scale.height * 0.62;
    const startY = cy + TOAST_ENTER_OFFSET_Y;
    const panel = new NineSlicePanel(s, cx, cy, {
      style: TOAST_PANEL_STYLE,
      width: TOAST_PANEL_WIDTH,
      height: TOAST_PANEL_HEIGHT,
      originX: 0.5,
      originY: 0.5,
    });
    panel.setDepth(DEPTH.toastPanel).setY(startY).setAlpha(0.5);
    s.add.existing(panel);
    const text = s.add
      .text(cx, startY, message, {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.body,
        color: PALETTE.textBright,
        align: "center",
        wordWrap: { width: TOAST_TEXT_MAX_WIDTH, useAdvancedWrap: true },
      })
      .setOrigin(0.5)
      .setDepth(DEPTH.toastText)
      .setAlpha(0.5);
    const hologramChrome = this.createHologramChrome(cx, cy);
    s.tweens.add({
      targets: [panel, text],
      y: cy,
      alpha: 1,
      duration: TOAST_ENTER_DURATION_MS,
      ease: "Quad.easeOut",
    });
    s.tweens.add({
      targets: hologramChrome,
      alpha: 1,
      duration: TOAST_ENTER_DURATION_MS,
      ease: "Quad.easeOut",
    });
    s.time.delayedCall(HOLOGRAM_SCAN_DELAY_MS, () => this.emitHologramScan(cx, cy));

    s.time.delayedCall(TOAST_ENTER_DURATION_MS + TOAST_HOLD_MS, () => {
      this.emitHologramCollapse(cx, cy);
      s.tweens.add({
        targets: [panel, ...hologramChrome],
        alpha: 0,
        scaleY: 0.22,
        duration: HOLOGRAM_FADE_DURATION_MS,
        ease: "Quad.easeIn",
      });
      s.tweens.add({
        targets: text,
        y: cy - 2,
        duration: TOAST_TEXT_EXIT_DURATION_MS,
        ease: "Quad.easeOut",
      });
      s.tweens.add({
        targets: text,
        alpha: 0,
        delay: TOAST_TEXT_ALPHA_DELAY_MS,
        duration: TOAST_TEXT_ALPHA_DURATION_MS,
        ease: "Quad.easeIn",
        onComplete: () => {
          for (const obj of hologramChrome) obj.destroy();
          panel.destroy();
          text.destroy();
        },
      });
    });
  }

  private createHologramChrome(cx: number, y: number): Phaser.GameObjects.GameObject[] {
    const s = this.scene;
    const chrome: Phaser.GameObjects.GameObject[] = [];

    for (const offsetY of [-18, 18]) {
      const edgeLine = s.add
        .rectangle(cx, y + offsetY, TOAST_PANEL_WIDTH - 52, 1, PALETTE_NUM.textBright, HOLOGRAM_EDGE_LINE_ALPHA)
        .setDepth(DEPTH.toastPanel)
        .setOrigin(0.5)
        .setAlpha(0.35);
      chrome.push(edgeLine);
    }

    for (const offsetY of [-TOAST_PANEL_HEIGHT / 2 - 4, TOAST_PANEL_HEIGHT / 2 + 4]) {
      const beam = s.add
        .rectangle(cx, y + offsetY, TOAST_PANEL_WIDTH - 96, 1, PALETTE_NUM.infoBlue, HOLOGRAM_BEAM_ALPHA)
        .setDepth(DEPTH.toastPanel)
        .setOrigin(0.5)
        .setAlpha(0.35);
      chrome.push(beam);
    }

    for (const sideX of [-1, 1]) {
      for (const sideY of [-1, 1]) {
        const x = cx + sideX * (TOAST_PANEL_WIDTH / 2 - 28);
        const yCorner = y + sideY * (TOAST_PANEL_HEIGHT / 2 - 10);
        const horizontal = s.add
          .rectangle(x, yCorner, 24, 2, PALETTE_NUM.infoBlue, HOLOGRAM_CORNER_ALPHA)
          .setDepth(DEPTH.toastPanel)
          .setOrigin(0.5)
          .setAlpha(0.35);
        const vertical = s.add
          .rectangle(x + sideX * 11, yCorner - sideY * 6, 2, 14, PALETTE_NUM.infoBlue, HOLOGRAM_CORNER_ALPHA)
          .setDepth(DEPTH.toastPanel)
          .setOrigin(0.5)
          .setAlpha(0.35);
        chrome.push(horizontal, vertical);
      }
    }

    return chrome;
  }

  private emitHologramScan(cx: number, cy: number): void {
    const s = this.scene;
    const topGlint = s.add
      .rectangle(cx - TOAST_PANEL_WIDTH / 2 + 46, cy - 18, 42, 2, PALETTE_NUM.textBright, 0.28)
      .setDepth(DEPTH.toastPanel)
      .setOrigin(0.5);
    const bottomGlint = s.add
      .rectangle(cx + TOAST_PANEL_WIDTH / 2 - 46, cy + 18, 42, 2, PALETTE_NUM.textBright, 0.22)
      .setDepth(DEPTH.toastPanel)
      .setOrigin(0.5);
    s.tweens.add({
      targets: topGlint,
      x: cx + TOAST_PANEL_WIDTH / 2 - 46,
      alpha: 0,
      duration: HOLOGRAM_SCAN_DURATION_MS,
      ease: "Sine.easeOut",
      onComplete: () => topGlint.destroy(),
    });
    s.tweens.add({
      targets: bottomGlint,
      x: cx - TOAST_PANEL_WIDTH / 2 + 46,
      alpha: 0,
      duration: HOLOGRAM_SCAN_DURATION_MS,
      ease: "Sine.easeOut",
      onComplete: () => bottomGlint.destroy(),
    });
  }

  private emitHologramCollapse(cx: number, cy: number): void {
    const s = this.scene;
    const topBeam = s.add
      .rectangle(cx, cy - 18, TOAST_PANEL_WIDTH - 42, 2, PALETTE_NUM.infoBlue, 0.62)
      .setDepth(DEPTH.toastText)
      .setOrigin(0.5);
    const bottomBeam = s.add
      .rectangle(cx, cy + 18, TOAST_PANEL_WIDTH - 42, 2, PALETTE_NUM.infoBlue, 0.5)
      .setDepth(DEPTH.toastText)
      .setOrigin(0.5);
    s.tweens.add({
      targets: [topBeam, bottomBeam],
      scaleX: 0.08,
      alpha: 0,
      duration: HOLOGRAM_COLLAPSE_DURATION_MS,
      ease: "Cubic.easeIn",
      onComplete: () => {
        topBeam.destroy();
        bottomBeam.destroy();
      },
    });
  }

  /** 게임뷰 중앙 토스트 — 큰 텍스트가 위로 떠오르며 페이드 (던전 실패 등 알림). */
  showCenterToast(message: string): void {
    const gv = sceneGameviewRect(this.scene);
    centerToast(this.scene, {
      x: this.scene.scale.width / 2,
      y: gv.intTop + gv.intH / 2,
      message,
      fontFamily: FONT_FAMILY,
    });
  }
}
